/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {ApiConfig} from '../../../api';
import {OBJECT_STATE, EXECUTION_STATUS} from '../../resources-constants';
import {Uuid} from '../../../../util/uuid';
import {AdnHttpService} from '../../../../util/adn-http.service';
import {createAdnHttpResource, createAdnHttpResourceSender} from '../../../../util/create-adn-http-resource';
import {LocalUserProfile} from '../../../../session/local-user-profile';
import {AuthHttpInterceptorService} from '../../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../../app/common/directives/error/api-error-handler.service';

/** Angular DI token for the adnExecutionResource HTTP resource (factory product). */
export class AdnExecutionResource {
}

export function createAdnExecutionResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile
): any {
  const send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);

  return function(resourcePath: string): any {
    const ExecutionResource: any = createAdnHttpResource(send, resourcePath, () => localUserProfile);
    const uri = ApiConfig.obtain().getVersionedUri(resourcePath);
    const withId = (id: any) => uri + '/' + encodeURIComponent(id);

    ExecutionResource.delete = function(report: any): any {
      return {
        $promise: send({
          method: 'POST',
          url: withId(report.id),
          body: {
            id: report.id,
            cancelRequested: true,
            objectState: OBJECT_STATE.hidden
          }
        }).then((savedReport: any) => _.set(savedReport, 'status', EXECUTION_STATUS.cancelled))
      };
    };

    ExecutionResource.cancel = function(report: any): any {
      return {
        $promise: send({
          method: 'POST',
          url: withId(report.id),
          body: {
            id: report.id,
            cancelRequested: true
          }
        })
      };
    };

    ExecutionResource.$start = function(params: any): Promise<any> {
      return ExecutionResource.save(_.assign({id: Uuid.generate()}, params)).$promise.then((execution: any) => {
        return new ExecutionResource(execution);
      });
    };

    ExecutionResource.prototype.$cancel = function(): Promise<any> {
      return ExecutionResource.cancel(this).$promise.then((execution: any) => {
        _.assign(this, execution);
        return this;
      });
    };

    ExecutionResource.prototype.inProgress = function(): boolean {
      return this.isPending() || this.isQueued() || this.isProcessing() || this.isSubmitted();
    };
    ExecutionResource.prototype.isPending = function(): boolean {
      return this.status === EXECUTION_STATUS.pending;
    };
    ExecutionResource.prototype.isSubmitted = function(): boolean {
      return this.status === EXECUTION_STATUS.submitted;
    };
    ExecutionResource.prototype.isProcessing = function(): boolean {
      return this.status === EXECUTION_STATUS.processing;
    };
    ExecutionResource.prototype.isCancelled = function(): boolean {
      return this.status === EXECUTION_STATUS.cancelled || this.cancelRequested;
    };
    ExecutionResource.prototype.isCompleted = function(): boolean {
      return this.status === EXECUTION_STATUS.completed;
    };
    ExecutionResource.prototype.isRejected = function(): boolean {
      return this.status === EXECUTION_STATUS.rejected;
    };
    ExecutionResource.prototype.isFailed = function(): boolean {
      return this.status === EXECUTION_STATUS.failed;
    };
    ExecutionResource.prototype.isQueued = function(): boolean {
      return this.status === EXECUTION_STATUS.queued;
    };
    ExecutionResource.prototype.isTimedOut = function(): boolean {
      return this.status === EXECUTION_STATUS.timedOut;
    };
    ExecutionResource.prototype.isAwaiting = function(): boolean {
      return this.isPending() || this.isQueued() || this.isSubmitted() || this.isProcessing();
    };
    ExecutionResource.prototype.hasStarted = function(): boolean {
      return !(this.isPending() || this.isQueued() || this.isSubmitted());
    };
    ExecutionResource.prototype.hasFailed = function(): boolean {
      return this.isRejected() || this.isFailed() || this.isTimedOut();
    };
    ExecutionResource.prototype.hasCompleted = function(): boolean {
      return this.isCancelled() || this.isCompleted();
    };

    return ExecutionResource;
  };
}

export const adnExecutionResourceProvider = {
  provide: AdnExecutionResource,
  useFactory: createAdnExecutionResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile]
};

